import React, { useState, FC } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from '../atoms/Container';
import Sidebar from '../atoms/Sidebar';
import BiampLogo from '../atoms/BiampLogo';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Box, List, ListItem, ListItemButton, ListItemText, Button, CircularProgress, Alert, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FooterWithLinks from '../sections/FooterWithLinks';
import useOrganizations from '../../hooks/useOrganizations';
import { useAuth } from '../../contexts/AuthContext';

const SelectOrg: FC = () => {
  // Fetch organizations using the custom hook
  const { organizations, loading, error, isUnactivated, refetch } = useOrganizations();
  const { signout } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  // Filter organizations based on search term
  const filteredOrganizations = organizations.filter(org =>
    org.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOrgSelect = (orgId: string, orgName: string) => {
    console.log('SelectOrg: Organization selected', { orgId, orgName });

    // Save organization ID and name to session storage
    sessionStorage.setItem('selectedOrgId', orgId);
    sessionStorage.setItem('selectedOrgName', orgName);

    console.log('SelectOrg: Saved to sessionStorage, navigating to /designs');
    // Redirect to designs list
    navigate('/designs');
  };

  const handleRetry = () => {
    refetch();
  };

  const handleSignOut = async () => {
    try {
      await signout();
      navigate('/');
    } catch (error) {
      console.error('Error during sign out:', error);
    }
  };

  return (
    <Container>
      <Sidebar>
        <BiampLogo />

        <ContentContainer>
          <Title>Select organization</Title>
          
          <SearchContainer>
            <TextField
              variant="outlined"
              fullWidth
              label = "Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '28px' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '3px',
                  color: 'white',
                  fontFamily: 'Open Sans',
                  fontSize: '14px',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.15)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.25)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&:hover': {

                  },
                  '&.Mui-focused': {
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiInputBase-input': {
                  color: 'white',
                  fontFamily: 'Open Sans',
                  fontSize: '14px',
                  '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1,
                  },
                },
                '& .MuiInputBase-root': {

                },
                '& .MuiOutlinedInput-notchedOutline': {

                },
              }}
            />
          </SearchContainer>
          
          <ScrollableListContainer>
            {loading ? (
              <LoadingContainer>
                <CircularProgress 
                  size={40} 
                  sx={{ color: 'rgba(255, 255, 255, 0.7)' }} 
                />
                <Typography 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)', 
                    fontFamily: 'Open Sans',
                    fontSize: '14px',
                    marginTop: '16px'
                  }}
                >
                  Loading organizations...
                </Typography>
              </LoadingContainer>
            ) : error ? (
              <ErrorContainer>
                <Alert 
                  severity={isUnactivated ? 'info' : 'error'}
                  sx={{ 
                    backgroundColor: isUnactivated ? 'rgba(2, 136, 209, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                    color: 'white',
                    border: `1px solid ${isUnactivated ? 'rgba(2, 136, 209, 0.3)' : 'rgba(211, 47, 47, 0.3)'}`,
                    '& .MuiAlert-icon': {
                      color: isUnactivated ? 'rgba(2, 136, 209, 0.8)' : 'rgba(211, 47, 47, 0.8)'
                    }
                  }}
                >
                  {error}
                </Alert>
                {isUnactivated ? (
                  <NoOrgCTA />
                ) : (
                  <Button
                    variant="outlined"
                    onClick={handleRetry}
                    sx={{
                      marginTop: '16px',
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      fontFamily: 'Open Sans',
                      fontSize: '12px',
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    Retry
                  </Button>
                )}
              </ErrorContainer>
            ) : filteredOrganizations.length > 0 ? (
              <List sx={{ width: '100%', padding: 0 }}>
                {filteredOrganizations.map((org) => (
                  <div key={org.id}>
                    <ListItem 
                      disablePadding
                      sx={{
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                      }}
                    >
                      <ListItemButton
                        onClick={() => handleOrgSelect(org.id, org.name)}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          },
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                        }}
                      >
                        <OrgLogo logoUrl={org.logoUrl} />
                        <ListItemText
                          primary={org.name}
                          sx={{
                            '& .MuiListItemText-primary': {
                              color: 'white',
                              fontSize: '16px',
                              fontFamily: 'Open Sans',
                              fontWeight: 600,
                            },
                          }}
                        />
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M15.9458 12.0824L8.08875 4.04881L9.16112 3L18.0607 12.0995L17.5759 12.5736L9.12511 21.0244L8.06445 19.9637L15.9458 12.0824Z" fill="white"/>
                        </svg>
                      </ListItemButton>
                    </ListItem>
                    <div style={{ height: '10px' }} /> {/* Spacer between items */}
                  </div>
                ))}
              </List>
            ) : (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                padding: '32px 16px',
                textAlign: 'center'
              }}>
                {searchTerm ? (
                  <Typography sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontFamily: 'Open Sans',
                    fontSize: '14px'
                  }}>
                    No organizations match your search criteria.
                  </Typography>
                ) : (
                  <NoOrgCTA />
                )}
              </Box>
            )}
          </ScrollableListContainer>
        </ContentContainer>

        <FooterWithLinks>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>

            <Box
                onClick={handleSignOut}
                sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginRight: '16px',
                    '&:hover': {
                    opacity: 0.8,
                    },
                }}
            >
            <img 
                src="/images/SignoutIcon.svg" 
                alt="Sign out" 
                style={{ width: '20px', height: '20px' }}
            />
            <span style={{
                color: '#ffffff',
                fontSize: '14px',
                fontFamily: 'Open Sans',
                fontWeight: 400,
            }}>
                Sign out
            </span>
            </Box>
           
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <span>2025 Biamp®</span>
              <span style={{ marginLeft: '12px' }}>
                <a 
                  href="https://www.biamp.com/legal/privacy-policy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: '#646464', 
                    fontSize: '14px',
                    fontFamily: 'Open Sans !important',
                    fontWeight: 400,
                    textDecoration: 'none'
                  }}
                >
                  Privacy
                </a>
              </span>
              <span style={{ marginLeft: '12px' }}>
                <a 
                  href="https://www.biamp.com/legal/terms-of-use" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: '#646464', 
                    fontSize: '14px',
                    fontFamily: 'Open Sans !important',
                    fontWeight: 400,
                    textDecoration: 'none'
                  }}
                >
                  Terms of Use
                </a>
              </span>
            </Box>
          </Box>
        </FooterWithLinks>
      </Sidebar>
    </Container>
  );
};

const Title = styled(Typography)({
  color: 'white',
  fontSize: '28px',
  fontFamily: 'Montserrat',
  fontWeight: 500,
  lineHeight: '28px',
  wordWrap: 'break-word',
});

const ScrollableListContainer = styled(Box)({
  flex: 1,
  padding: '0 5px 0 0',
  maxHeight: '450px', // Limit height to show maximum 5 items (approximately 90px per item)
  width: '100%', // Width for the list
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '3px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
    },
  },
});

const ContentContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  flex: 1,
  gap: '24px',
  width: '100%',
});

const SearchContainer = styled(Box)({
  width: '99%',
  marginBottom: '8px',
});

interface OrgLogoProps {
  logoUrl?: string;
}

const OrgLogo: React.FC<OrgLogoProps> = ({ logoUrl }) => {
  return (
    <Box
      sx={{
        width: '64px',
        height: '64px',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        overflow: 'hidden',
        position: 'relative',
        backgroundImage: logoUrl ? 'none' : 'url("/images/DefaultOrgIcon.svg")',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: 'contain',
      }}
    >
      {logoUrl && (
        <img
          src={logoUrl}
          alt="Organization Logo"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            borderRadius: '6px',
          }}
          onError={(e) => {
            // If image fails to load, hide it and show the placeholder
            e.currentTarget.style.display = 'none';
          }}
        />
      )}
    </Box>
  );
};

const LoadingContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '150px',
});

const ErrorContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  padding: '16px',
});

const NoOrgCTA: FC = () => {
  const workplaceHomeUrl = process.env.REACT_APP_WORKPLACE_HOME_URL || 'https://stage.workplace.biamp.app';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '30px', mt: 2 }}>
      <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontFamily: 'Open Sans', fontSize: '14px', textAlign: 'center' }}>
        You are not a member of any organization. Create or join one to get started.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'center', width: '100%', justifyContent: 'space-between'}}>
        <Button
          variant="outlined"
          href={`${workplaceHomeUrl}/organizations/join-organization`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.3)',
            fontFamily: 'Open Sans',
            fontSize: '14px',
            textTransform: 'none',
            width: '50%',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          Join Organization
        </Button>
        <Button
          variant="outlined"
          href={`${workplaceHomeUrl}/organizations/create-organization`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.3)',
            fontFamily: 'Open Sans',
            fontSize: '14px',
            textTransform: 'none',
                        width: '50%',

            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          Create Organization
        </Button>
      </Box>
    </Box>
  );
};

export default SelectOrg;
